
import React, { useState, useEffect } from 'react';
import { ShoppingBag, Eye, TrendingUp, X, Sparkles, MapPin } from 'lucide-react';
import { storage } from '../lib/storage';
import { Order, Product, Store } from '../types';

interface SocialProofPopupProps {
  storeId: string;
}

const CITIES = ['Mumbai', 'Delhi', 'Bangalore', 'London', 'New York', 'Tokyo', 'Berlin', 'Hyderabad', 'Pune'];

const SocialProofPopup: React.FC<SocialProofPopupProps> = ({ storeId }) => {
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [config, setConfig] = useState(storage.getStoreConfig(storeId).socialProof);

  useEffect(() => {
    const handleUpdate = () => {
      setConfig(storage.getStoreConfig(storeId).socialProof);
    };
    window.addEventListener('store-branding-update', handleUpdate);
    return () => window.removeEventListener('store-branding-update', handleUpdate);
  }, [storeId]);

  useEffect(() => {
    if (!config?.enabled) return;

    const generateNotification = () => {
      const orders = storage.getOrders().filter(o => o.storeId === storeId);
      const products = storage.getProducts().filter(p => p.storeId === storeId);
      
      const type = Math.random();
      
      if (type < 0.4 && orders.length > 0 && config.showRecentSales) {
        const order = orders[Math.floor(Math.random() * orders.length)];
        const product = order.items?.[0]?.product || products[0];
        const city = CITIES[Math.floor(Math.random() * CITIES.length)];
        setCurrentNotification({
          icon: <ShoppingBag className="h-4 w-4" />,
          title: "New Purchase",
          text: `Someone in ${city} just bought a ${product.name}`,
          time: "2 mins ago",
          color: "bg-blue-600"
        });
      } else if (type < 0.8 && config.showLiveViewers) {
        const product = products[Math.floor(Math.random() * products.length)];
        const count = Math.floor(Math.random() * 25) + 5;
        setCurrentNotification({
          icon: <Eye className="h-4 w-4" />,
          title: "Live Activity",
          text: `${count} people are viewing ${product.name} right now`,
          color: "bg-indigo-600"
        });
      } else if (config.showStockAlerts) {
        const product = products.find(p => p.stock > 0 && p.stock < 10) || products[0];
        setCurrentNotification({
          icon: <TrendingUp className="h-4 w-4" />,
          title: "Hot Seller",
          text: `Hurry! Only ${product.stock} units left of ${product.name}`,
          color: "bg-orange-600"
        });
      }

      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 5000);
    };

    const interval = setInterval(generateNotification, (config.intervalSeconds || 15) * 1000);
    // Trigger initial notification after 5s
    const timeout = setTimeout(generateNotification, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [storeId, config]);

  if (!currentNotification || !isVisible) return null;

  return (
    <div className="fixed bottom-6 left-6 z-[90] animate-in slide-in-from-left-10 fade-in duration-700">
      <div className="bg-white/80 backdrop-blur-xl border border-white/40 shadow-2xl rounded-[2rem] p-4 flex items-center gap-4 max-w-sm ring-1 ring-black/5">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-lg ${currentNotification.color}`}>
          {currentNotification.icon}
        </div>
        <div className="flex-grow min-w-0 pr-4">
          <div className="flex items-center gap-2 mb-0.5">
             <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">{currentNotification.title}</span>
             <Sparkles className="h-2 w-2 text-blue-500 animate-pulse" />
          </div>
          <p className="text-xs font-black text-gray-900 leading-tight truncate">
            {currentNotification.text}
          </p>
          {currentNotification.time && (
            <p className="text-[8px] text-gray-400 font-bold uppercase mt-1 tracking-tighter">{currentNotification.time}</p>
          )}
        </div>
        <button onClick={() => setIsVisible(false)} className="p-1 hover:bg-gray-100 rounded-full text-gray-300 hover:text-gray-600 transition-all">
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};

export default SocialProofPopup;
